import {useMemo, useCallback} from 'react'
import {useGrowthBook} from '@/contexts/GrowthBookContext'
import {useUserPermissions} from '@/hooks/useUserPermissions'
import {canAccess, type AccessControlMap} from '@/services/accessControl/resolver'

const ACL_FEATURE_KEY = 'emd-access-control'

/**
 * Hook to check access control for one or more slugs.
 *
 * Usage:
 *   const {hasAccess} = useAccess()
 *   if (hasAccess('page:ai-productivity')) { ... }
 *
 *   // Or check a specific slug directly:
 *   const {allowed} = useAccessSlug('page:ai-productivity')
 */
export const useAccess = () => {
  const {features, isLoading} = useGrowthBook()
  const {userInfo} = useUserPermissions()

  const acl: AccessControlMap = useMemo(() => {
    const raw = features[ACL_FEATURE_KEY]
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      return raw as AccessControlMap
    }
    return {}
  }, [features])

  // ACL is considered loaded when GrowthBook is done loading AND we have the ACL feature
  const aclLoaded = useMemo(() => {
    return !isLoading && features[ACL_FEATURE_KEY] !== undefined
  }, [isLoading, features])

  const userRoles: string[] = useMemo(() => userInfo?.roles ?? [], [userInfo])

  const hasAccess = useCallback((slug: string): boolean => canAccess(slug, userRoles, acl), [userRoles, acl])

  return {hasAccess, acl, userRoles, isLoading, aclLoaded}
}

/**
 * Convenience hook for a single slug — returns a boolean directly.
 *
 * Usage:
 *   const {allowed, loading} = useAccessSlug('page:admin-dashboard')
 */
export const useAccessSlug = (slug: string) => {
  const {features, isLoading} = useGrowthBook()
  const {userInfo} = useUserPermissions()

  const allowed = useMemo(() => {
    const raw = features[ACL_FEATURE_KEY]
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false
    const acl = raw as AccessControlMap
    const roles = userInfo?.roles ?? []
    return canAccess(slug, roles, acl)
  }, [features, userInfo, slug])

  return {allowed, loading: isLoading}
}
